import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MLIcon from '../MLIcon';
import './Pagination.css';

const Pagination = ({
  activePage,
  defaultActivePage,
  totalPages,
  boundaryRange = 1,
  siblingRange = 1,
  ellipsisItem = '...',
  firstItem = {
    'aria-label': 'First item',
    content: '«',
  },
  lastItem = {
    'aria-label': 'Last item',
    content: '»',
  },
  prevItem = {
    'aria-label': 'Previous item',
    content: <div><MLIcon type="chevron_left" /></div>,
  },
  nextItem = {
    'aria-label': 'Next item',
    content: <div><MLIcon type="chevron_right" /></div>,
  },
  onPageChange,
  className = '',
  'aria-label': ariaLabel = 'Page Navigation',
  ...otherProps
}) => {

  const [currentPage, setCurrentPage] = useState(activePage || defaultActivePage || 1);
  const [disablePrevItemArrow, setDisablePrevItemArrow] = useState(false);
  const [disableNextItemArrow, setDisableNextItemArrow] = useState(false);

  useEffect(() => {
    if (activePage !== undefined) {
      setCurrentPage(activePage);
    }
  }, [activePage]);

  useEffect(() => {
    setDisablePrevItemArrow(currentPage === 1);
    setDisableNextItemArrow(currentPage === totalPages);
  }, [currentPage, totalPages]);

  const handlePageChange = useCallback((event, pageNumber) => {
    if (activePage === undefined) {
      setCurrentPage(pageNumber);
    }
    
    if (onPageChange) {
      onPageChange(event, { activePage: pageNumber, totalPages });
    }
  }, [activePage, onPageChange, totalPages]);

  const renderPageNumbers = () => {
    const pages = [];
    const showEllipsisStart = currentPage > boundaryRange + siblingRange + 2;
    const showEllipsisEnd = currentPage < totalPages - boundaryRange - siblingRange - 1;

    // First pages
    for (let i = 1; i <= Math.min(boundaryRange, totalPages); i++) {
      pages.push(i);
    }

    // Start ellipsis
    if (showEllipsisStart) {
      pages.push('ellipsis-start');
    }

    // Pages around current page
    const startPage = Math.max(boundaryRange + 1, currentPage - siblingRange);
    const endPage = Math.min(totalPages - boundaryRange, currentPage + siblingRange);
    
    for (let i = startPage; i <= endPage; i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    // End ellipsis
    if (showEllipsisEnd) {
      pages.push('ellipsis-end');
    }

    // Last pages
    for (let i = Math.max(totalPages - boundaryRange + 1, 1); i <= totalPages; i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    return pages;
  };

  const renderItem = (item, type) => {
    if (!item) return null;
    
    if (React.isValidElement(item)) {
      return item;
    }
    
    if (typeof item === 'object' && item.content) {
      return item.content;
    }
    
    return item;
  };

  const classes = classNames(
    'ng-ui-widgets',
    'ng-ui-pagination',
    className,
    {
      'disable-prev-item': disablePrevItemArrow,
      'disable-next-item': disableNextItemArrow,
    }
  );

  return (
    <nav className={classes} aria-label={ariaLabel} {...otherProps}>
      {/* First Item */}
      {firstItem && (
        <a
          className="ng-ui-pagination-navigation-item"
          onClick={(e) => !disablePrevItemArrow && handlePageChange(e, 1)}
          aria-label={firstItem['aria-label'] || 'First page'}
          aria-disabled={disablePrevItemArrow}
          tabIndex={disablePrevItemArrow ? -1 : 0}
        >
          {renderItem(firstItem)}
        </a>
      )}

      {/* Previous Item */}
      {prevItem && (
        <a
          className={classNames('ng-ui-pagination-navigation-item', 'ng-ui-pagination-prev-item')}
          onClick={(e) => !disablePrevItemArrow && handlePageChange(e, currentPage - 1)}
          aria-label={prevItem['aria-label'] || 'Previous page'}
          aria-disabled={disablePrevItemArrow}
          tabIndex={disablePrevItemArrow ? -1 : 0}
        >
          {renderItem(prevItem)}
        </a>
      )}

      {/* Page Numbers */}
      {renderPageNumbers().map((page, index) => {
        if (page === 'ellipsis-start' || page === 'ellipsis-end') {
          return (
            <span
              key={`ellipsis-${page}`}
              className="ng-ui-pagination-ellipsis-item"
              aria-hidden="true"
            >
              {renderItem(ellipsisItem)}
            </span>
          );
        }

        return (
          <a
            key={page}
            className={classNames('ng-ui-pagination-page-item', {
              'active': page === currentPage,
            })}
            onClick={(e) => handlePageChange(e, page)}
            aria-label={`Page ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
            tabIndex={0}
          >
            {page}
          </a>
        );
      })}

      {/* Next Item */}
      {nextItem && (
        <a
          className={classNames('ng-ui-pagination-navigation-item', 'ng-ui-pagination-next-item')}
          onClick={(e) => !disableNextItemArrow && handlePageChange(e, currentPage + 1)}
          aria-label={nextItem['aria-label'] || 'Next page'}
          aria-disabled={disableNextItemArrow}
          tabIndex={disableNextItemArrow ? -1 : 0}
        >
          {renderItem(nextItem)}
        </a>
      )}

      {/* Last Item */}
      {lastItem && (
        <a
          className="ng-ui-pagination-navigation-item"
          onClick={(e) => !disableNextItemArrow && handlePageChange(e, totalPages)}
          aria-label={lastItem['aria-label'] || 'Last page'}
          aria-disabled={disableNextItemArrow}
          tabIndex={disableNextItemArrow ? -1 : 0}
        >
          {renderItem(lastItem)}
        </a>
      )}
    </nav>
  );
};



Pagination.propTypes = {
  activePage: PropTypes.number,
  'aria-label': PropTypes.string,
  boundaryRange: PropTypes.number,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  defaultActivePage: PropTypes.number,
  ellipsisItem: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.object,
    ])),
  ]),
  firstItem: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.object,
    ])),
  ]),
  lastItem: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.object,
    ])),
  ]),
  nextItem: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.object,
    ])),
  ]),
  onPageChange: PropTypes.func,
  siblingRange: PropTypes.number,
  prevItem: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.object,
    ])),
  ]),
  totalPages: PropTypes.number.isRequired,
};

export default Pagination;